import { useCallback, useRef } from 'react'
import { useAppStore } from '@/store/app-store'
import { runBenchmark } from '@/engine/benchmark-runner'
import { saveResults } from '@/engine/api-client'
import { runPreflight } from '@/engine/preflight'
import { applyBedrockRegion, getVendorFromPreset } from '@/utils/vendor-filter'
import type { PreflightResult } from '@/engine/preflight'

export function useBenchmark() {
  const {
    isRunning, progress, currentRun,
    setRunning, setProgress, clearStreamChunks,
    setCurrentRun, addPastRun, addNetworkProbe, clearNetworkData
  } = useAppStore()

  const abortRef = useRef<AbortController | null>(null)

  const startBenchmark = useCallback(
    async (opts: { iterations: number }): Promise<{ preflightErrors?: PreflightResult[] }> => {
      const { profiles, presets, bedrockRegion } = useAppStore.getState()
      const regionAppliedPresets = presets.map(p =>
        getVendorFromPreset(p) === 'bedrock' ? applyBedrockRegion(p, bedrockRegion) : p
      )
      const allProfiles = [...regionAppliedPresets, ...profiles]
      const enabledProfiles = allProfiles.filter(p => p.enabled)

      // Pre-flight validation
      const preflightResults = await runPreflight(enabledProfiles)
      const failures = preflightResults.filter(r => !r.ok)
      if (failures.length > 0) {
        // Return failures so the UI can display them - don't start the benchmark
        return { preflightErrors: failures }
      }

      setRunning(true)
      setProgress(null)
      clearStreamChunks()
      clearNetworkData()
      setCurrentRun(null)

      abortRef.current = new AbortController()

      try {
        const run = await runBenchmark({
          profiles: allProfiles,
          iterations: opts.iterations,
          signal: abortRef.current.signal,
          onProgress: (p) => setProgress(p),
          onNetworkProbe: (probe) => addNetworkProbe(probe)
        })
        setCurrentRun(run)
        addPastRun(run)
        // Auto-save to S3 for chat Q&A (fire and forget)
        saveResults(run).catch(err => console.warn('Failed to save results to S3:', err))
        return {}
      } finally {
        setRunning(false)
        setProgress(null)
        abortRef.current = null
      }
    },
    [setRunning, setProgress, clearStreamChunks, clearNetworkData, setCurrentRun, addPastRun, addNetworkProbe]
  )

  const cancelBenchmark = useCallback(() => {
    abortRef.current?.abort()
    setRunning(false)
    setProgress(null)
  }, [setRunning, setProgress])

  return { isRunning, progress, currentRun, startBenchmark, cancelBenchmark }
}
